// Task 6.1 — Page Intégrations Google (Calendar + Gmail)
import { useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Mail, CalendarCheck } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../ui/Card';
import {
  useGoogleCalendarConnection,
  useDisconnectGoogle,
} from '../../hooks/queries/useGoogleCalendarConnection';
import {
  startGoogleConnect,
  startGoogleConnectServices,
  disconnectGoogleService,
} from '../../services/googleSync/googleSyncService';
import { OutlookIntegrationCard } from './OutlookIntegrationCard';

export function GoogleIntegrationsTab() {
  const { data: conn, isLoading, isError } = useGoogleCalendarConnection();
  const disconnectAll = useDisconnectGoogle();
  const [connecting, setConnecting] = useState<string | null>(null);
  const [params] = useSearchParams();
  const justConnected = params.get('google') === 'connected';
  const errored = params.get('google') === 'error';

  const scopes: string[] = conn?.scopes ?? [];
  const hasCalendar = scopes.some((s) => s.includes('calendar'));
  const hasGmail = scopes.some((s) => s.includes('gmail') || s.includes('mail.google'));

  const onConnectAll = async () => {
    setConnecting('all');
    try {
      await startGoogleConnect();
    } catch (err) {
      setConnecting(null);
      toast.error(err instanceof Error ? err.message : 'Échec de la connexion Google');
    }
  };

  const onEnableService = async (service: string) => {
    setConnecting(service);
    try {
      await startGoogleConnectServices([service]);
    } catch (err) {
      setConnecting(null);
      toast.error(err instanceof Error ? err.message : `Échec d'activation ${service}`);
    }
  };

  const onDisableService = async (service: string) => {
    setConnecting(service);
    try {
      await disconnectGoogleService(service);
      toast.success(`${service === 'calendar' ? 'Calendrier' : 'Gmail'} déconnecté.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Échec de déconnexion ${service}`);
    } finally {
      setConnecting(null);
    }
  };

  return (
    <div className="space-y-6">
    <Card
      header={
        <div className="flex items-center gap-2">
          <CalendarCheck className="w-4 h-4 text-cime-primary" aria-hidden />
          <span className="text-sm font-semibold text-neutral-700">
            Intégrations Google
          </span>
        </div>
      }
    >
      <p className="text-sm text-neutral-500 mb-4">
        Connectez votre compte Google Workspace pour synchroniser vos échéances et
        auto-rattacher les pièces jointes des e-mails clients aux dossiers.
      </p>

      {errored && (
        <p className="text-sm text-red-600 mb-3">
          La connexion à Google a échoué. Réessayez.
        </p>
      )}

      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin text-neutral-400" aria-hidden />
      ) : isError ? (
        <p className="text-sm text-red-600">
          Impossible de vérifier la connexion. Réessayez plus tard.
        </p>
      ) : conn && conn.status === 'active' ? (
        <div className="space-y-4">
          {/* Compte connecté */}
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-neutral-700">
                Connecté : <strong>{conn.google_email}</strong>
              </p>
              {conn.last_sync_at && (
                <p className="text-xs text-neutral-400 mt-0.5">
                  Dernière sync :{' '}
                  {new Date(conn.last_sync_at).toLocaleString('fr-FR')}
                </p>
              )}
            </div>
            <button
              onClick={() => disconnectAll.mutate()}
              disabled={disconnectAll.isPending}
              className="text-xs text-red-600 hover:underline disabled:opacity-50 shrink-0 mt-0.5"
            >
              {disconnectAll.isPending ? 'Déconnexion…' : 'Tout déconnecter'}
            </button>
          </div>

          {/* Toggles par service */}
          <div className="divide-y divide-neutral-100 border border-neutral-200 rounded-md">
            <ServiceRow
              icon={<CalendarCheck className="w-4 h-4 text-neutral-500" aria-hidden />}
              label="Calendrier"
              description="Pousse vos audiences et tâches vers Google Calendar. Seuls le type d'échéance, la date et le numéro RG sont transmis — aucun nom de client."
              enabled={hasCalendar}
              loading={connecting === 'calendar'}
              onEnable={() => onEnableService('calendar')}
              onDisable={() => onDisableService('calendar')}
            />
            <ServiceRow
              icon={<Mail className="w-4 h-4 text-neutral-500" aria-hidden />}
              label="Gmail"
              description="Auto-rattache les pièces jointes des e-mails clients aux dossiers concernés via leur objet ou numéro RG."
              enabled={hasGmail}
              loading={connecting === 'gmail'}
              onEnable={() => onEnableService('gmail')}
              onDisable={() => onDisableService('gmail')}
            />
          </div>

          {justConnected && (
            <p className="text-sm text-green-600">
              Compte Google connecté. La synchronisation démarre automatiquement.
            </p>
          )}
        </div>
      ) : conn && conn.status !== 'active' ? (
        <div className="space-y-2">
          <p className="flex items-center gap-2 text-sm text-amber-700">
            <AlertTriangle className="w-4 h-4" aria-hidden />
            {conn.status === 'revoked'
              ? 'Accès Google révoqué.'
              : 'Erreur de synchronisation.'}
            {conn.last_error && (
              <span className="text-xs text-neutral-400">({conn.last_error})</span>
            )}
          </p>
          <button
            onClick={onConnectAll}
            disabled={connecting !== null}
            className="text-sm font-medium text-cime-primary hover:underline disabled:opacity-50"
          >
            Reconnecter
          </button>
        </div>
      ) : (
        <button
          onClick={onConnectAll}
          disabled={connecting !== null}
          className="rounded-md bg-cime-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {connecting ? 'Redirection…' : 'Connecter mon compte Google'}
        </button>
      )}
    </Card>
    <OutlookIntegrationCard />
    </div>
  );
}

function ServiceRow(props: {
  icon: React.ReactNode;
  label: string;
  description: string;
  enabled: boolean;
  loading: boolean;
  onEnable: () => void;
  onDisable: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-3">
      <div className="flex items-start gap-3">
        <span className="mt-0.5">{props.icon}</span>
        <div>
          <p className="text-sm font-medium text-neutral-700">{props.label}</p>
          <p className="text-xs text-neutral-500 mt-0.5">{props.description}</p>
        </div>
      </div>
      <button
        onClick={props.enabled ? props.onDisable : props.onEnable}
        disabled={props.loading}
        className={[
          'shrink-0 rounded px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50',
          props.enabled
            ? 'border border-red-300 text-red-600 hover:bg-red-50'
            : 'bg-cime-primary text-white hover:opacity-90',
        ].join(' ')}
      >
        {props.loading
          ? '…'
          : props.enabled
          ? 'Désactiver'
          : 'Activer'}
      </button>
    </div>
  );
}
